<template>
  <LoadingLayout :isLoading='query.isLoading()'>
    <fieldset class='space-y-2' :id='labelId'>
      <legend :class='{"sr-only": props.hideLabel}'>{{ props.label }}</legend>
      <SearchInput v-model='state.keywords' />
      <details v-for="theme in themes" :key="themeCollection.getId(theme)" class='rounded-md border-0 shadow-sm ring-1 ring-inset ring-gray-300 py-3 px-4 text-sm' open>
        <summary><strong class='font-medium'>{{ themeCollection.getName(theme) }}</strong></summary>
        <ul class='mt-2 space-y-2'>
          <template v-for="objective in theme.objectives" :key="objectiveCollection.getId(objective)">
            <li v-if='availabilityCheck(objective)'>
              <label class='flex items-start gap-2'>
                <input type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-jananavy-600 focus:ring-jananavy-600" :value="objectiveCollection.getId(objective)" v-model="state.selected" />
                <details class='text-sm'>
                  <summary><strong class='font-medium'>{{ objectiveCollection.getName(objective) }}</strong></summary>
                  <div class='mt-2 rounded-md bg-gray-100 py-3 px-4 text-sm'>
                    <p class='border-b pb-2 mb-2'>{{ objectiveCollection.getObjective(objective) }}</p>
                    <p>{{ objectiveCollection.getQuestion(objective) }}</p>
                  </div>
                </details>
              </label>
            </li>
          </template>
        </ul>
      </details>
    </fieldset>
  </LoadingLayout>
</template>

<script setup>
  
import { nanoid } from 'nanoid';

import { reactive, computed, watch } from 'vue';

import { usePayloadQuery } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import SearchInput from '@/components/inputs/search-input.vue';

const props = defineProps({
  label: String,
  hideLabel: Boolean,
  labelId: String
});

const labelId = computed(() => {
  return props.labelId || nanoid();
});

const state = reactive({
  selected: [],
  keywords: null
});

const model = defineModel();

const selectedCache = [];

const objectiveCollection = getCollection('objective');
const themeCollection = getCollection('theme');

const query = usePayloadQuery(objectiveCollection.getApiIndexEndpoint(), {
  sort: objectiveCollection.getDefaultSort(),
  limit: 1000
});

const themes = computed(() => {
  const themes = {};
  
  query.getItems().forEach((item) => {
    if (state.keywords && !item.name.toLowerCase().includes(state.keywords.toLowerCase())) {
      return;
    }
    
    const themeId = themeCollection.getId(item.theme);
    
    if (!themes[themeId]) {
      themes[themeId] = {
        ...item.theme,
        objectives: []
      }
    }
    
    themes[themeId].objectives.push(item);
  });
  
  Object.values(themes).forEach((theme) => {
    theme.objectives.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  });
  
  return Object.values(themes).toSorted((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const modelDidChange = () => {
  if (isModelSynced()) {
    return;
  }
  
  state.selected = getModelIds();
  selectedCache.push(...state.selected);
}

const selectedDidChange = () => {
  if (isModelSynced()) {
    return;
  }
  
  model.value = state.selected.map(key => query.getItems().find(item => objectiveCollection.getId(item) === key));
}

const getModelIds = () => {
  return model.value?.length ? model.value.map(item => objectiveCollection.getId(item)) : [];
}

const isModelSynced = () => {
  return (JSON.stringify(getModelIds().sort()) === JSON.stringify(state.selected.sort()));
}

const availabilityCheck = (objective) => {
  if (objective.isPublished !== true) {
    return false;
  }
  
  if (objective.isDeprecated !== true) {
    return true;
  }
  
  return selectedCache.includes(objectiveCollection.getId(objective));
}

watch(model, () => {
  modelDidChange();
});

watch(() => state.selected, () => {
  selectedDidChange();
});

modelDidChange();

query.doQuery();

</script>
